/**
 * The day boundary.
 *
 * A "study day" ends at 04:00 local, not midnight. Night Owl is a core
 * identity in this product — a student grinding until 1am is mid-session, not
 * starting tomorrow, and rolling over at midnight would silently break their
 * streak at the exact moment they were working hardest.
 *
 * Every derived number in the app (streaks, "today", daily goals, Early Bird
 * vs Night Owl) resolves through `studyDate` so the rule exists once.
 */
export const DAY_ROLLOVER_HOUR = 4;

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

/** Days since the Unix epoch → civil Y/M/D, by arithmetic only.
 *  Deliberately not `new Date()`: core stays free of ambient time. */
function civilFromDays(days: number): { y: number; m: number; d: number } {
  const z = days + 719_468;
  const era = Math.floor(z / 146_097);
  const doe = z - era * 146_097;
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1460) + Math.floor(doe / 36_524) - Math.floor(doe / 146_096)) / 365,
  );
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp < 10 ? mp + 3 : mp - 9;
  const y = yoe + era * 400 + (m <= 2 ? 1 : 0);
  return { y, m, d };
}

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

/** `YYYY-MM-DD` — an opaque day key, never parsed back into a timestamp. */
export type StudyDate = string;

/**
 * Which study day a moment belongs to.
 *
 * @param atMs            UTC milliseconds.
 * @param utcOffsetMinutes Minutes east of UTC at that moment (Dhaka = +360).
 *                        Passed in per-event rather than derived, so history
 *                        stays correct across travel and DST.
 */
export function studyDate(atMs: number, utcOffsetMinutes: number): StudyDate {
  const localMs = atMs + utcOffsetMinutes * MS_PER_MINUTE;
  const shifted = localMs - DAY_ROLLOVER_HOUR * 60 * MS_PER_MINUTE;
  const { y, m, d } = civilFromDays(Math.floor(shifted / MS_PER_DAY));
  return `${y}-${pad(m)}-${pad(d)}`;
}

/** Whole study days between two day keys — the streak primitive. */
export function daysBetween(from: StudyDate, to: StudyDate): number {
  return Math.round((dayIndex(to) - dayIndex(from)));
}

/** Day key → integer day number, for ordering and gap maths. */
export function dayIndex(date: StudyDate): number {
  const [ys, ms, ds] = date.split('-');
  const y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  // days_from_civil, the inverse of civilFromDays.
  const yAdj = y - (m <= 2 ? 1 : 0);
  const era = Math.floor(yAdj / 400);
  const yoe = yAdj - era * 400;
  const doy = Math.floor((153 * (m > 2 ? m - 3 : m + 9) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146_097 + doe - 719_468;
}

/** Local hour-of-day under the same rollover rule — feeds Night Owl / Early
 *  Bird detection, which is why it lives next to the boundary definition. */
export function localHour(atMs: number, utcOffsetMinutes: number): number {
  const localMs = atMs + utcOffsetMinutes * MS_PER_MINUTE;
  return Math.floor((localMs % MS_PER_DAY) / 3_600_000);
}
